import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { axiosInstance } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Briefcase, Calendar, BookOpen, MessageSquare, ArrowRight } from "lucide-react";

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState({
    jobs: 0,
    events: 0,
    posts: 0,
    threads: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [jobsRes, eventsRes, postsRes, threadsRes] = await Promise.all([
        axiosInstance.get("/jobs"),
        axiosInstance.get("/events"),
        axiosInstance.get("/blog"),
        axiosInstance.get("/threads")
      ]);
      
      setStats({
        jobs: jobsRes.data.length,
        events: eventsRes.data.length,
        posts: postsRes.data.length,
        threads: threadsRes.data.length
      });
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  return (
    <div data-testid="dashboard-page" className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
          Welcome back, {user.name}!
        </h1>
        <p className="text-lg text-gray-600">
          Your community hub for jobs, events, articles, and discussions
        </p>
      </div>

      {/* Quick Access Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-white/90 backdrop-blur-sm border-teal-100 hover:shadow-lg transition-all">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Calendar className="w-6 h-6 text-teal-600" />
              </div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Merriweather, serif' }}>
                Events
              </CardTitle>
            </div>
            <CardDescription className="text-base">
              Browse and create community events and meetups
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/app/events">
              <Button className="w-full bg-teal-500 hover:bg-teal-600 rounded-full" data-testid="goto-events">
                View Events <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="bg-white/90 backdrop-blur-sm border-amber-100 hover:shadow-lg transition-all">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-lg">
                <BookOpen className="w-6 h-6 text-amber-600" />
              </div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Merriweather, serif' }}>
                Articles
              </CardTitle>
            </div>
            <CardDescription className="text-base">
              Share helpful articles, resources, and links
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/app/blog">
              <Button className="w-full bg-amber-500 hover:bg-amber-600 rounded-full" data-testid="goto-blog">
                View Articles <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="bg-white/90 backdrop-blur-sm border-orange-100 hover:shadow-lg transition-all">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <MessageSquare className="w-6 h-6 text-orange-600" />
              </div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Merriweather, serif' }}>
                Discussions
              </CardTitle>
            </div>
            <CardDescription className="text-base">
              Engage in community threads and conversations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/app/threads">
              <Button className="w-full bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="goto-discussions">
                Join Discussions <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="bg-white/90 backdrop-blur-sm border-orange-100 hover:shadow-lg transition-all">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Briefcase className="w-6 h-6 text-orange-600" />
              </div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Merriweather, serif' }}>
                Job Board
              </CardTitle>
            </div>
            <CardDescription className="text-base">
              Discover and post job opportunities in the Seattle area
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/app/jobs">
              <Button className="w-full bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="goto-jobs">
                Browse Jobs <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
